/**
 * Which registration desk this machine is, remembered by the machine (D86).
 *
 * Both numbers a registration carries — `REG-0042` and the number worn in the arena — are allocated
 * by reading the highest one issued and adding one. With one desk that is right. With two desks that
 * cannot see each other, it is two people told they are 42, and nobody finds out until the numbers
 * meet: at a gate, in a judge's running order, or on a payout sheet. Telling a desk *which desk it
 * is* gives it its own band of the sequence, and then two desks cannot collide whether or not they
 * can reach each other or the internet.
 *
 * **A property of the machine, not of the person.** One registrar works whichever table is short of
 * hands, and their account follows them; the folding table and the laptop on it do not move. So this
 * lives beside `scan-mode`, in the same storage, set once when the desk is set up.
 *
 * **Unset is the ordinary case and must stay free.** Nearly every community runs one desk. It sends
 * no station, the server allocates across the whole event exactly as it always has, and the numbers
 * are the plain 1, 2, 3 that get called across an arena. Nothing about a single-desk event changes.
 */

import { MAX_STATION, STATION_BLOCK_SIZE } from "@powwow/validation";
import { deviceId } from "./device.js";

const KEY = "pw.station";

/**
 * The desk number this machine has been given, or null for "not set — one desk".
 *
 * Never throws. A kiosk profile with storage locked down registers perfectly well; it just cannot
 * remember which desk it is, which is the same state as a desk that was never told.
 */
export function deskStation(): number | null {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  const parsed = Number(raw);
  // Anything outside the bands the server will accept is treated as unset rather than sent and
  // refused — the desk that would see the refusal is the one in front of a queue, not the one that
  // mistyped this weeks ago.
  if (!Number.isInteger(parsed) || parsed < 1 || parsed > MAX_STATION) return null;
  return parsed;
}

/** Set which desk this machine is, or clear it back to a single-desk event. */
export function setDeskStation(station: number | null): void {
  try {
    if (station === null) localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, String(station));
  } catch {
    /* Private browsing or a kiosk profile: registration still works, the desk just forgets. */
  }
}

/** The band of numbers a desk issues from, for saying so on screen before anybody relies on it. */
export function stationBandLabel(station: number): string {
  const base = (station - 1) * STATION_BLOCK_SIZE;
  const format = (value: number) => value.toLocaleString("en-CA");
  return `${format(base + 1)}–${format(base + STATION_BLOCK_SIZE - 1)}`;
}

/**
 * What a desk sends with everything it registers, or nothing at all when it is the only desk.
 *
 * The device id rides along only beside a station, because it is only ever read to tell two machines
 * on the *same* desk apart. On a single-desk event there is nothing to tell apart, and sending an
 * identifier that would never be looked at is not something to do by default.
 */
export function stationBody(): { station?: number; stationDeviceId?: string } {
  const station = deskStation();
  return station === null ? {} : { station, stationDeviceId: deviceId() };
}

/**
 * Whether another machine has also been registering as this desk.
 *
 * Answered from what the server has actually seen rather than from a claim made at setup: a laptop
 * set to Desk 2 and never used is not a second desk, and the mistake only matters once both are
 * taking registrations. `devices` holds every machine the server has seen on that desk, this one
 * included, so more than one of them is the warning.
 */
export function otherDevicesOnThisDesk(
  stations: ReadonlyArray<{ station: number; devices: string[] }>,
  station: number,
): number {
  const found = stations.find((entry) => entry.station === station);
  if (!found) return 0;
  const mine = deviceId();
  return found.devices.filter((id) => id !== mine).length;
}
